import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useShift } from '../-components/use-shift';
import type { ComponentProps } from 'react';
import { can } from '@minty/shared/permissions';

import { Skeleton } from '~/components/ui/skeleton';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/settings')({
  pendingComponent: SettingsLayoutPending,
  component: SettingsLayoutRoute,
});

const SettingsSidebarLink = ({ ...props }: ComponentProps<typeof Link>) => {
  return (
    <Link
      {...props}
      className="rounded-lg px-3 py-2 text-sm font-normal text-foreground hover:bg-background hover:text-accent-foreground data-[status=active]:bg-background data-[status=active]:font-medium data-[status=active]:text-foreground data-[status=active]:shadow-sm data-[status=active]:ring-1 data-[status=active]:ring-border"
      activeOptions={{ exact: true, includeSearch: false }}
    />
  );
};

function SettingsLayoutRoute() {
  const { t } = useTranslation('settings');
  const trpc = useTRPC();
  const { data: currentUser } = useSuspenseQuery(
    trpc.auth.session.getCurrentUser.queryOptions(),
  );
  const { shiftsEnabled } = useShift();
  const { data: browserNotifications } = useSuspenseQuery(
    trpc.notifications.subscriptions.status.queryOptions({
      endpointHash: null,
    }),
  );

  return (
    // Sidebar and content are centred together as one block. The whole page
    // scrolls, so the scrollbar stays at the window edge; the sidebar sticks.
    <div className="h-full w-full overflow-auto">
      <div className="mx-auto flex w-full max-w-[1100px] items-start">
        <aside className="sticky top-0 flex w-64 shrink-0 flex-col space-y-1 px-3 py-5">
          <SettingsSidebarLink to="/settings/account">
            {t('Account')}
          </SettingsSidebarLink>
          <SettingsSidebarLink to="/settings/users">
            {t('Users')}
          </SettingsSidebarLink>
          {can(currentUser.roles, 'integrations') && (
            <SettingsSidebarLink to="/settings/google-workspace">
              {t('Google Workspace')}
            </SettingsSidebarLink>
          )}
          <SettingsSidebarLink to="/settings/calls">
            {t('Calls')}
          </SettingsSidebarLink>
          {browserNotifications.available && (
            <SettingsSidebarLink to="/settings/notifications">
              {t('Notifications')}
            </SettingsSidebarLink>
          )}
          {shiftsEnabled && (
            <SettingsSidebarLink to="/settings/shifts">
              {t('Shifts')}
            </SettingsSidebarLink>
          )}
          {can(currentUser.roles, 'businessHours') && (
            <SettingsSidebarLink to="/settings/work-hours">
              {t('Office Working Hours')}
            </SettingsSidebarLink>
          )}
        </aside>
        {/* One width for every settings page, so switching pages never
            reflows the column. */}
        <div className="flex max-w-[820px] min-w-0 flex-1 flex-col space-y-3 px-3 py-5">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

function SettingsLayoutPending() {
  return (
    <div className="h-full w-full overflow-auto">
      <div className="mx-auto flex w-full max-w-[1100px] items-start">
        <aside
          className="flex w-64 shrink-0 flex-col gap-2 px-3 py-5"
          aria-busy="true"
        >
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-9 w-full rounded-lg" />
          ))}
        </aside>
        <div className="max-w-[820px] min-w-0 flex-1 px-3 py-5">
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}
